import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Collapse,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { orderBy } from "lodash-es";
import { useMemo, useState } from "react";
import { notEmpty } from "../../../../utils/not-empty";
import { ChannelIcon } from "../../../channels/components/channel-icon";
import { prettyChannelName } from "../../../channels/utils";
import { PreviewDialogLink } from "./moment-segment-preview/components/preview-dialog-link";
import { usePersonsInSegmentJson } from "./moment-segment-preview/hooks/use-persons-in-segment-json";
import { PreviewDatagrid } from "./moment-segment-preview/preview-datagrid";
import {
  notEnabledDescription,
  people,
  plural,
  suppressedDescription,
} from "./moment-segment-preview/utils";

import type { ChannelName, ChannelStatus } from "../../../channels/types";
import type { GetPersonsInSegmentJsonPerson, Segment } from "../../types/segment-schema";
import type { FC, ReactNode } from "react";

type Channel = NonNullable<ChannelName>;

/** The people, or for a reviewer just how many, as they may not see who */
type People = GetPersonsInSegmentJsonPerson[] | number;

interface ChannelBreakdown {
  name: Channel;
  /** Have the channel and are not opted out */
  recipients: People;
  /** Have the channel but are opted out or blocked */
  unable: People;
  /** Don't have the channel */
  missing: People;
}

export interface SummaryCounts {
  members: People;
  channels: ChannelBreakdown[];
}

const count = (persons: People): number => (typeof persons === "number" ? persons : persons.length);

const CHANNEL_ORDER: Channel[] = [
  "work_email",
  "slack",
  "teams",
  "twilio",
  "personal_email",
  "whatsapp",
];

interface SegmentGroupProps {
  segment: Segment["segment_json"] | Segment["segment_json"]["segments"][number];
  channels: ChannelName[];
  type: "full" | "group";
  /** The channel the Communication sends on, to summarise just that channel */
  channel?: ChannelName;
  /** The Communication is about the segment rather than to it, so only its members count */
  membersOnly?: boolean;
}

export const SegmentPreview: FC<SegmentGroupProps> = ({
  segment,
  channels,
  type,
  channel = null,
  membersOnly = false,
}) => {
  const { data, error, loading } = usePersonsInSegmentJson(
    segment.type === "segment" && segment.segments.length === 0 ? null : { segment_json: segment },
  );

  const summary = useMemo(
    (): SummaryCounts | null =>
      data
        ? {
            members: data,
            channels: channels.filter(notEmpty).map((c) => {
              const result = {
                name: c,
                recipients: [] as GetPersonsInSegmentJsonPerson[],
                unable: [] as GetPersonsInSegmentJsonPerson[],
                missing: [] as GetPersonsInSegmentJsonPerson[],
              };
              for (const person of data) {
                // oxlint-disable-next-line typescript/no-unsafe-type-assertion
                const channelInfo = (person.channels as Record<Channel, ChannelStatus>)[c];
                if (!channelInfo) result.missing.push(person);
                else if (channelInfo.opted_out) result.unable.push(person);
                else result.recipients.push(person);
              }
              return result;
            }),
          }
        : null,
    [channels, data],
  );

  if (error) {
    console.error("Error loading persons for segment:", error);
    return (
      <SummaryPanel type={type}>
        <Alert severity="error">
          {type === "group" ? "Error loading people in group" : "Error loading people in segment"}
        </Alert>
      </SummaryPanel>
    );
  }

  if (loading || !summary)
    return (
      <SummaryPanel type={type}>
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
      </SummaryPanel>
    );

  return (
    <SegmentSummary summary={summary} type={type} channel={channel} membersOnly={membersOnly} />
  );
};

/** The summary of a group, or of the whole segment in its own panel */
export const SegmentSummary: FC<{
  summary: SummaryCounts;
  type: "full" | "group";
  channel?: ChannelName;
  membersOnly?: boolean;
}> = ({ summary, type, channel = null, membersOnly = false }) => {
  const breakdown = orderBy(summary.channels, (c) => CHANNEL_ORDER.indexOf(c.name));
  const selected = channel && breakdown.find((c) => c.name === channel);
  const where = type === "group" ? "group" : "segment";

  return (
    <SummaryPanel type={type}>
      {membersOnly ? (
        <Members persons={summary.members} type={type} />
      ) : selected ? (
        <ChannelSummary persons={summary.members} selected={selected} where={where}>
          <Breakdown persons={summary.members} breakdown={breakdown} type={type} />
        </ChannelSummary>
      ) : (
        <Breakdown persons={summary.members} breakdown={breakdown} type={type} />
      )}
    </SummaryPanel>
  );
};

const SummaryPanel: FC<{ type: "full" | "group"; children: ReactNode }> = ({ type, children }) => {
  if (type === "group") return children;

  return (
    <Box
      data-testid="segment-summary"
      sx={{
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "info.main",
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
      }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle2">Segment summary</Typography>
        {children}
      </Stack>
    </Box>
  );
};

/** "{Channel} recipients in this group: 25 of 64 in group, 3 unable to receive, 36 don't have {Channel} enabled" */
const ChannelSummary: FC<{
  persons: People;
  selected: ChannelBreakdown;
  where: "group" | "segment";
  children: ReactNode;
}> = ({ persons, selected, where, children }) => {
  const [open, setOpen] = useState(false);
  const channelName = prettyChannelName(selected.name);
  const { recipients, unable, missing } = selected;

  const unableText = count(unable) > 0 && (
    <>
      <CountLink
        persons={unable}
        title={`${people(count(unable))} ${plural(count(unable), "is", "are")} not currently receiving ${channelName}`}
        description={suppressedDescription[selected.name]}
        analyticsId="segment-unable-count"
      />
      {" unable to receive"}
    </>
  );
  const missingText = count(missing) > 0 && (
    <>
      <CountLink
        persons={missing}
        title={`People without ${channelName}`}
        description={notEnabledDescription[selected.name]}
        analyticsId="segment-missing-count"
      />
      {` ${plural(count(missing), "doesn’t", "don’t")} have ${channelName} enabled`}
    </>
  );

  return (
    <Stack spacing={1}>
      <Label
        text={
          where === "group"
            ? `${channelName} recipients in this group`
            : `${channelName} recipients`
        }
        tooltip={`People in this ${where} who can currently be sent ${channelName} Communications`}
      />
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography variant="h5" component="p">
          <CountLink
            persons={recipients}
            channelName={selected.name}
            title={`People who can receive ${channelName}`}
            analyticsId="segment-recipients-count"
          />
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`of ${count(persons)} in ${where}`}
          {(unableText || missingText) && " "}
          {unableText}
          {unableText && missingText && ", "}
          {missingText}
        </Typography>
      </Stack>
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={() => setOpen((o) => !o)}
        data-analytics-id="segment-breakdown-toggle"
        sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center" }}
      >
        {`View full ${where} breakdown`}
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Link>
      <Collapse in={open}>{children}</Collapse>
    </Stack>
  );
};

/** Members, then recipients, unable to receive and not enabled for every channel */
const Breakdown: FC<{
  persons: People;
  breakdown: ChannelBreakdown[];
  type: "full" | "group";
}> = ({ persons, breakdown, type }) => (
  <Stack direction="row" spacing={4} useFlexGap sx={{ flexWrap: "wrap" }}>
    <Members persons={persons} type={type} />
    <Stack spacing={1}>
      <Label
        text="Recipients by channel"
        tooltip="People who can currently be sent Communications on each channel"
      />
      <ChannelCounts breakdown={breakdown} kind="recipients" />
    </Stack>
    <Stack spacing={1}>
      <Label
        text="Unable to receive channels"
        tooltip="People who have the channel but aren’t currently receiving it, e.g. they’ve blocked messages or opted out"
      />
      <ChannelCounts breakdown={breakdown} kind="unable" />
    </Stack>
    <Stack spacing={1}>
      <Label
        text="Channels not enabled"
        tooltip="People we can’t find an account or address for on each channel"
      />
      <ChannelCounts breakdown={breakdown} kind="missing" />
    </Stack>
  </Stack>
);

const Members: FC<{ persons: People; type: "full" | "group" }> = ({ persons, type }) => (
  <Stack spacing={1}>
    <Label
      text={type === "group" ? "Current group members" : "Current total segment members"}
      tooltip="Everyone who currently matches these conditions"
    />
    <Typography variant="h5" component="p">
      <CountLink
        persons={persons}
        title={type === "group" ? "People in this group" : "People in this segment"}
        analyticsId="people-in-segment-count"
      />
    </Typography>
  </Stack>
);

/** Recipients are listed for every channel; unable and missing only where there are some */
const ChannelCounts: FC<{
  breakdown: ChannelBreakdown[];
  kind: "recipients" | "unable" | "missing";
}> = ({ breakdown, kind }) => {
  const shown = kind === "recipients" ? breakdown : breakdown.filter((c) => count(c[kind]) > 0);

  if (shown.length === 0)
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        None
      </Typography>
    );

  return (
    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {shown.map((c) => {
        const channelName = prettyChannelName(c.name);
        const persons = c[kind];
        return (
          <Stack key={c.name} direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <ChannelIcon channel={c.name} fontSize="small" />
            <Typography variant="body2">
              {kind === "recipients" && (
                <CountLink
                  persons={persons}
                  channelName={c.name}
                  title={`People who can receive ${channelName}`}
                  analyticsId={`view-recipients-${c.name}-button`}
                />
              )}
              {kind === "unable" && (
                <CountLink
                  persons={persons}
                  title={`${people(count(persons))} ${plural(count(persons), "is", "are")} not currently receiving ${channelName}`}
                  description={suppressedDescription[c.name]}
                  analyticsId={`view-unable-${c.name}-button`}
                />
              )}
              {kind === "missing" && (
                <CountLink
                  persons={persons}
                  title={`People without ${channelName}`}
                  description={notEnabledDescription[c.name]}
                  analyticsId={`view-missing-${c.name}-button`}
                />
              )}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

/** A count that opens the people it counts, or just the count when there are none or only the count is known */
const CountLink: FC<{
  persons: People;
  title: string;
  description?: string;
  channelName?: ChannelName;
  analyticsId: string;
}> = ({ persons, title, description, channelName = null, analyticsId }) => {
  if (typeof persons === "number" || persons.length === 0) return <>{count(persons)}</>;

  return (
    <PreviewDialogLink
      text={`${persons.length}`}
      title={title}
      description={description && <Typography variant="body2">{description}</Typography>}
      analyticsId={analyticsId}
      closeAnalyticsId={`${analyticsId}-close`}
    >
      <PreviewDatagrid channelName={channelName} persons={persons} />
    </PreviewDialogLink>
  );
};

const Label: FC<{ text: string; tooltip: string }> = ({ text, tooltip }) => (
  <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
    <Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
      {text}
    </Typography>
    <Tooltip title={tooltip}>
      <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
    </Tooltip>
  </Stack>
);
